import React from 'react';
import { MdClose } from 'react-icons/md';
import { CheckSquare, Users, Globe, Lock } from 'lucide-react';
import useScrollLock from '../../../hooks/useScrollLock';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store/hooks';
import { createTeamTask, Task, updateTeamTask } from '../../../store/slices/teamFocusSessionSlice';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { taskFormSchema, TaskForm } from '../../../schemas/task';

interface TaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialTask?: Task | null;
}

const TaskModal: React.FC<TaskModalProps> = ({ isOpen, onClose, initialTask }) => {
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm<TaskForm>({
        resolver: zodResolver(taskFormSchema),
        defaultValues: {
            title: '',
            description: '',
            status: 'todo',
            assignedToId: '',
            isPublic: true,
        }
    });

    const isPublic = watch('isPublic');

    const { teamId } = useParams<{ teamId: string }>();
    const dispatch = useAppDispatch();

    const user = useSelector((state: RootState) => state.user.user);
    const session = useSelector((state: RootState) => teamId ? state.teamFocusSessions.sessions[teamId] : null);
    const teamMembers = session?.teamMembers || [];

    useScrollLock(isOpen);

    React.useEffect(() => {
        if (isOpen) {
            if (initialTask) {
                // build a raw object matching the form schema
                const raw: any = {
                    title: initialTask.title || '',
                    description: initialTask.description || '',
                    status: initialTask.status || 'todo',
                    is_public: initialTask.is_public ?? true,
                };

                // Extract assigned ID
                let aId = '';
                if (initialTask.assigned_to) {
                    aId = typeof initialTask.assigned_to === 'object'
                        ? initialTask.assigned_to.id
                        : initialTask.assigned_to;
                }
                raw.assignedToId = aId || '';

                // populate fields via reset
                reset(raw);
            } else {
                reset({
                    title: '',
                    description: '',
                    status: 'todo',
                    assignedToId: '',
                    isPublic: true,
                });
            }
        }
    }, [isOpen, initialTask, reset]);

    const onFormSubmit = (data: TaskForm) => {
        // basic existence check for outside data
        if (!teamId || !user?.id) {
            console.error("Missing teamId or userId");
            return;
        }

        // assemble assignedTo object as before
        let assignedToData: any = null;
        if (data.assignedToId) {
            if (data.assignedToId === user.id) {
                assignedToData = {
                    id: user.id,
                    name: user.name || 'User',
                    avatar: (user as any).avatar || ''
                };
            } else {
                const member = teamMembers.find(m => m.id === data.assignedToId);
                if (member) {
                    assignedToData = {
                        id: member.id,
                        name: member.name || 'User',
                        avatar: member.avatar || ''
                    };
                }
            }
        }

        const taskData = {
            title: data.title,
            description: data.description || undefined,
            status: data.status,
            assigned_to: assignedToData ? {
                id: assignedToData.id,
                name: assignedToData.name
            } : null,
            is_public: data.isPublic
        };

        const handleTaskSubmit = async () => {
            try {
                if (initialTask) {
                    await dispatch(updateTeamTask({
                        teamId,
                        taskId: initialTask.id,
                        updates: taskData,
                    })).unwrap();
                    toast.success("Task updated successfully!");
                } else {
                    await dispatch(createTeamTask({
                        teamId,
                        task: taskData,
                    })).unwrap();
                    toast.success("Task added successfully!");
                }
                onClose();
            } catch (error: any) {
                toast.error(error || "Failed to save task");
                console.error("Error saving task:", error);
            }
        };

        handleTaskSubmit();
    };

    if (!isOpen) return null;

    const titleValue = watch('title');

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-[#FFF8F0] rounded-2xl p-6 sm:p-8 max-w-lg w-full border border-gray-200/60 shadow-xl overflow-y-auto max-h-[90vh]">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                        <CheckSquare className="w-6 h-6 text-orange-600" />
                        <h3 className="text-2xl font-medium text-gray-900">{initialTask ? 'Edit Task' : 'New Task'}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
                    >
                        <MdClose />
                    </button>
                </div>

                <div className="space-y-5">
                    {Object.keys(errors).length > 0 && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded">
                            {Object.values(errors).map((e: any, idx) => (
                                <p key={idx} className="text-sm">
                                    {e.message}
                                </p>
                            ))}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Task Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            {...register('title')}
                            placeholder="What needs to be done?"
                            className="w-full px-4 py-2.5 bg-white border border-gray-200/60 rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300"
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Description <span className="text-gray-400 font-normal">(Optional)</span>
                        </label>
                        <textarea
                            {...register('description')}
                            placeholder="Add more details..."
                            rows={3}
                            className="w-full px-4 py-2.5 bg-white border border-gray-200/60 rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 resize-none"
                        />
                    </div>

                    {initialTask && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                Status
                            </label>
                            <select
                                {...register('status')}
                                className="w-full px-4 py-2.5 bg-white border border-gray-200/60 rounded-xl text-gray-900 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300"
                            >
                                <option value="todo">To Do</option>
                                <option value="inprogress">In Progress</option>
                                <option value="done">Done</option>
                            </select>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                <Users className="w-4 h-4 text-gray-500" />
                                Assign To
                            </label>
                            <select
                                {...register('assignedToId')}
                                className="w-full px-3 py-2.5 bg-white border border-gray-200/60 rounded-xl text-gray-900 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300"
                            >
                                <option value="">Unassigned</option>
                                {user && (
                                    <option value={user.id}>Me ({user.name})</option>
                                )}
                                {teamMembers
                                    .filter(m => m.id !== user?.id)
                                    .map(member => (
                                        <option key={member.id} value={member.id}>
                                            {member.name}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                {isPublic ? <Globe className="w-4 h-4 text-gray-500" /> : <Lock className="w-4 h-4 text-gray-500" />}
                                Visibility
                            </label>
                            <div className="flex items-center gap-3 mt-2">
                                <button
                                    type="button"
                                    onClick={() => setValue('isPublic', true)}
                                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${isPublic
                                        ? 'bg-orange-50 text-orange-700 border border-orange-200 relative z-10'
                                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    Team
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setValue('isPublic', false)}
                                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${!isPublic
                                        ? 'bg-orange-50 text-orange-700 border border-orange-200 relative z-10'
                                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    Private
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 rounded-xl font-medium transition-all duration-300 text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit(onFormSubmit)}
                            disabled={!titleValue?.trim()}
                            className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 text-sm ${!titleValue?.trim()
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-orange-600 hover:bg-orange-700 text-white shadow-sm hover:shadow'
                                }`}
                        >
                            {initialTask ? 'Save Changes' : 'Create Task'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskModal;
